import {useEffect, useRef} from 'react';
import {useAtomValue, useSetAtom} from 'jotai';
import {isOnlineAtom, authStatusAtom} from './authAtoms';
import {syncAllPendingRunTicketPhotosAtom} from './runTicketPhotoSync';
import {runTicketsLoadingAtom} from './runTicketAtoms';

/**
 * Sibling to useOfflineSync, but for run ticket photos (see
 * lib/offlineDb.ts's pending-photos store / state/runTicketPhotoSync.ts)
 * — kept as a separate hook because a photo's own sync step is
 * upload-then-attach, not just "replay one queued mutation," and
 * because photo cleanup must not be tied to the ticket record's own
 * mutation succeeding (see the 2026-08-20 fix to offlineQueue.ts's
 * applySyncResultAtom).
 *
 * Mounted globally in App.tsx alongside useOfflineSync so a photo
 * captured while offline gets uploaded the moment connectivity returns,
 * no matter what page the user is on by then. Also covers the "app was
 * closed with a pending photo and reopened already online" case, same
 * as useOfflineSync's own hydration-triggered flush — every mount with
 * isOnline already true runs this once.
 */
export function useOfflinePhotoSync() {
   const isOnline = useAtomValue(isOnlineAtom);
   // FIXED 2026-08-31 — same un-auth-gated gap fixed in useOfflineSync.ts
   // (see its own comment): mounted globally in App.tsx alongside it,
   // same exposure if a pending photo upload/attach survived a sign-out.
   const authStatus = useAtomValue(authStatusAtom);
   const isAuthed = authStatus === 'authenticated' || authStatus === 'offline-cached';
   // FIXED 2026-09-11 (Duane: "why does uploadRunTicketPhoto get called
   // on refresh, I wasn't doing anything with run tickets"): this used
   // to fire the instant isAuthed+isOnline went true, with no regard for
   // whether runTicketsAtom (useRunTicketReconciliation.ts's
   // RunTicket_AllWhere query, mounted at the same time, right next to
   // this hook in App.tsx) had actually loaded yet. On a fresh page
   // load both hooks start at once, so this could — and, per that
   // query's real network latency, routinely did — run its "does this
   // RunTicket already exist" check against a still-empty runTicketsAtom,
   // treating a perfectly real, already-saved RunTicket as "doesn't exist
   // yet" purely from the race, not any actual data problem. See
   // runTicketPhotoSync.ts's own ROOT CAUSE comment for the other half
   // of this fix (the genuinely-orphaned-forever case). runTicketsLoading
   // starts `true` and only ever goes true->false once per mount (a
   // plain Apollo `loading`, not `notifyOnNetworkStatusChange` — it
   // doesn't flip back on later 30s polls), so this simply waits for
   // that one transition before ever running the sync pass.
   const runTicketsLoading = useAtomValue(runTicketsLoadingAtom);
   const syncAll = useSetAtom(syncAllPendingRunTicketPhotosAtom);
   const syncingRef = useRef(false);

   useEffect(() => {
      if (!isAuthed || !isOnline || runTicketsLoading || syncingRef.current) return;
      syncingRef.current = true;
      syncAll().finally(() => {
         syncingRef.current = false;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isAuthed, isOnline, runTicketsLoading]);
}
